import React, { useEffect, useRef, useState } from "react";
import Drawer from "../../../../../../../components/SideDrawer/SideDrawer";
import { ViewingStatus } from "../utils/types";
import type {
  ViewingsData,
  ViewingQA,
} from "../../../../Viewings/utils/types";
import CloseIcon from "../../../../../../../assets/svg/close-icon";
import { ViewingStatusColors } from "../utils/constants";
import CalendarIcon from "../../../../../../../assets/svg/calendar-icon";
import ClockIcon from "../../../../../../../assets/svg/clock-icon";
import HomeIcon from "../../../../../../../assets/svg/home-icon";
import { Tooltip } from "../../../../../../../components/Tooltip/Tooltip";
import RoomTypeIcon from "../../../../../../../assets/svg/room-type-icon";
import Input from "../../../../../../../components/Input/Input";
import AddIcon from "../../../../../../../assets/svg/add-icon";
import Button from "../../../../../../../components/Button/Button";
import {
  useAddViewingQAMutation,
  useUpdateViewingQAMutation,
} from "../../../../../../../redux/services/crmApi";
import { useLazyGetViewingByIdQuery } from "../../../../../../../redux/services/viewingsApi";
import { customToast } from "../../../../../../../utils/custom-toast/custom-toast";
import QuestionandanswerIcon from "../../../../../../../assets/svg/questionandanswer-icon";
import { AppDispatch, RootState } from "../../../../../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { getViewingLabels } from "../../../../../../../utils/constants/mappings";
import ViewingsIcon from "../../../../../../../assets/svg/viewings-icon";
import VirtualViewingIcon from "../../../../../../../assets/svg/virtual-viewing-icon";
import { clearDrawer } from "../../../../../../../redux/slices/drawerSlice";
import { getFormattedScheduleDateTime } from "../../../../Viewings/utils/functions";
import ViewingQADrawerShimmer from "./ViewingQADrawerShimmer";

const ViewingQADrawer = ({ viewingId }: { viewingId: string }) => {
  const dispatch = useDispatch<AppDispatch>();

  const client = useSelector((state: RootState) => state?.auth?.client);
  const isDarkMode = useSelector(
    (state: RootState) => state?.themeProvider?.isDarkMode
  );
  const drawerState = useSelector((state: RootState) => state.drawer);

  const open =
    drawerState?.open && drawerState?.type === "viewing_qa";

  const [viewing, setViewing] = useState<ViewingsData | null>(null);
  const [viewingQA, setViewingQA] = useState<ViewingQA[]>([]);
  const [originalViewingQA, setOriginalViewingQA] = useState<ViewingQA[]>([]);
  const [newQuestion, setNewQuestion] = useState("");
  const [showAddCustomQuestion, setShowAddCustomQuestion] = useState(false);

  const [updateViewingQA, { isLoading: isUpdatingViewingQA }] =
    useUpdateViewingQAMutation();
  const [addViewingQA, { isLoading: isAddingViewingQA }] =
    useAddViewingQAMutation();
  const [fetchViewingById, { isFetching: isFetchingViewing }] =
    useLazyGetViewingByIdQuery();

  const qaScrollRef = useRef<HTMLDivElement>(null);

  const viewingLabels = getViewingLabels(
    client?.region,
    client?.client_code === "verbaflo"
  );

  const status: ViewingStatus =
    (viewing?.status as ViewingStatus) ?? ViewingStatus.SCHEDULED;
  const isVirtual = viewing?.viewing_type === "virtual";
  const TitleTypeIcon = isVirtual ? VirtualViewingIcon : ViewingsIcon;
  const titleIcon = (
    <TitleTypeIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk" />
  );
  const formattedScheduleDateTime = viewing?.scheduled_time
    ? getFormattedScheduleDateTime(viewing?.scheduled_time)
    : { date: "", time: "" };

  const handleSubmit = async () => {
    try {
      await updateViewingQA({
        viewingId,
        data: {
          crm_questions_and_answers: viewingQA,
        },
      })?.unwrap();
      customToast(
        `${viewingLabels.singular} QA updated successfully`,
        "success"
      );
      dispatch(clearDrawer());
    } catch (error) {
      console.log(error);
      customToast(`Failed to update ${viewingLabels.singular} QA`, "error");
    }
  };

  const handleAddViewingQA = async () => {
    try {
      const payload = {
        viewingId,
        data: {
          crm_questions_and_answers: [
            {
              question: newQuestion,
              answer: [""],
            },
          ],
        },
      };
      await addViewingQA(payload)?.unwrap();
      const updatedViewing = await fetchViewingById(viewingId)?.unwrap();
      setViewing(updatedViewing);
      setViewingQA(updatedViewing?.crm_questions_and_answers ?? []);
      setOriginalViewingQA(updatedViewing?.crm_questions_and_answers ?? []);
      customToast(`${viewingLabels.singular} QA added successfully`, "success");
      setShowAddCustomQuestion(false);
      setNewQuestion("");
      requestAnimationFrame(() => {
        const qaScrollContainer = qaScrollRef?.current;
        if (qaScrollContainer) {
          qaScrollContainer.scrollTo({
            top: qaScrollContainer.scrollHeight,
            behavior: "smooth",
          });
        }
      });
    } catch (error) {
      console.log(error);
      customToast(`Failed to add ${viewingLabels.singular} QA`, "error");
    }
  };

  const handleClose = () => {
    setViewingQA(originalViewingQA);
    setNewQuestion("");
    setShowAddCustomQuestion(false);
    dispatch(clearDrawer());
  };

  const handleAnswerChange = (index: number, answer: string) => {
    const updatedQA = viewingQA?.map((qa, i) =>
      i === index
        ? {
            question_id: qa?.question_id,
            question: qa?.question,
            answer: [answer],
          }
        : qa
    );
    setViewingQA(updatedQA);
  };

  const hasAnswersChanged = () => {
    if (!viewingQA || !originalViewingQA) return false;
    if (viewingQA.length !== originalViewingQA.length) return true;

    return viewingQA.some((qa, index) => {
      const originalAnswer = originalViewingQA[index]?.answer?.[0] || "";
      const currentAnswer = qa?.answer?.[0] || "";
      return originalAnswer !== currentAnswer;
    });
  };

  const isSaveButtonDisabled =
    isUpdatingViewingQA || viewingQA?.length === 0 || !hasAnswersChanged();

  useEffect(() => {
    if (!open || !viewingId) return;
    fetchViewingById(viewingId)
      ?.unwrap()
      ?.then((freshViewing) => {
        setViewing(freshViewing);
        const freshQA = freshViewing?.crm_questions_and_answers ?? [];
        setViewingQA(freshQA);
        setOriginalViewingQA(freshQA);
      })
      ?.catch(() => {});
  }, [open, viewingId, fetchViewingById]);

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      direction="right"
      size="650px"
      overlayColor="rgba(0,0,0,0.7)"
      duration={300}
      closeOnOverlayClick={true}
      closeOnEscape={true}
      drawerContainerClassName="rounded-l-[8px]"
    >
      {isFetchingViewing ? (
        <ViewingQADrawerShimmer />
      ) : (
      <div className="flex flex-col gap-6 py-3 px-4.5 h-full">
        <div className="flex items-center justify-between gap-2">
          <p className="flex gap-1 items-center text-base  text-heading-text dark:text-heading-text-dk">
            {titleIcon}
            {viewing?.virtual_meet_details
              ? viewingLabels.virtual
              : `On-Site ${viewingLabels.singular}`}{" "}
            Questions and Answers
          </p>
          <button
            className="w-[16px] h-[16px] bg-gray-100 dark:bg-gray-100-dk rounded-full flex flex-col items-center justify-center"
            aria-label="close create user modal"
            onClick={handleClose}
          >
            <CloseIcon className="w-[6px] h-[6px] text-verba-indigo dark:text-verba-indigo-dk" />
          </button>
        </div>

        <div
          className="flex gap-4 items-center p-3 rounded-lg border border-border-gray dark:border-border-gray-dk border-l-[6px]"
          style={{
            borderLeftColor: `${
              isDarkMode
                ? ViewingStatusColors[status]?.dark.primary
                : ViewingStatusColors[status]?.light.primary
            }`,
            backgroundColor: `${
              isDarkMode
                ? ViewingStatusColors[status]?.dark.secondary
                : ViewingStatusColors[status]?.light.secondary
            }`,
          }}
        >
          {(() => {
            const preferredPropertyName =
              viewing?.property_details?.chosen_properties?.[0]?.property_name;
            const preferredRoomType = viewing?.property_details?.room_type;

            const headerItems = [
              {
                key: "date",
                label: "Date",
                icon: (
                  <CalendarIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk shrink-0" />
                ),
                content: (
                  <p className="truncate">{formattedScheduleDateTime?.date}</p>
                ),
              },
              {
                key: "time",
                label: "Time",
                icon: (
                  <ClockIcon className="w-[12px] h-[12px] text-gray-placeholder dark:text-gray-placeholder-dk shrink-0" />
                ),
                content: (
                  <p className="truncate">
                    {formattedScheduleDateTime?.time}
                    <span className="text-gray-placeholder dark:text-gray-placeholder-dk text-xxs">
                      {" (Local Time)"}
                    </span>
                  </p>
                ),
              },
              ...(preferredPropertyName
                ? [
                    {
                      key: "preferred-property",
                      label: "Preferred Property",
                      icon: (
                        <HomeIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk shrink-0" />
                      ),
                      content: (
                        <Tooltip
                          content={preferredPropertyName}
                          className="cursor-default truncate"
                        >
                          <p className="truncate">{preferredPropertyName}</p>
                        </Tooltip>
                      ),
                    },
                  ]
                : []),
              ...(preferredRoomType
                ? [
                    {
                      key: "preferred-room",
                      label: "Preferred Room",
                      icon: (
                        <RoomTypeIcon className="w-[16px] h-[16px] text-gray-placeholder dark:text-gray-placeholder-dk shrink-0" />
                      ),
                      content: (
                        <Tooltip
                          content={preferredRoomType}
                          className="cursor-default truncate"
                        >
                          <p className="truncate">{preferredRoomType}</p>
                        </Tooltip>
                      ),
                    },
                  ]
                : []),
            ];

            return (
              <div className="flex gap-4 items-center w-full">
                {headerItems.map((item, index) => (
                  <React.Fragment key={item.key}>
                    {index > 0 && (
                      <div className="w-px h-[19px] border-l border-border-gray dark:border-border-gray-dk shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-275 dark:text-gray-275-dk text-xs">
                        {item.label}
                      </p>
                      <div className="flex gap-1 items-center text-md font-medium text-verba-indigo dark:text-verba-indigo-dk min-w-0">
                        {item.icon}
                        {item.content}
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            );
          })()}
        </div>

        <div
          ref={qaScrollRef}
          className="flex flex-col gap-3.5 h-full overflow-y-auto no-scrollbar"
        >
          {viewingQA?.map((question, index) => (
            <div
              key={`qa-${question?.question_id}-${index}`}
              className="flex flex-col gap-2"
            >
              <p className="text-md text-verba-field-label dark:text-verba-field-label-dk">
                Q.{index + 1} {question?.question}
              </p>
              <Input
                value={viewingQA[index]?.answer[0]}
                onChange={(e) => {
                  handleAnswerChange(index, e?.target?.value);
                }}
                secondaryContainerClassName="w-full border border-border-gray dark:border-border-gray-dk rounded-lg p-2"
                inputClassName="w-full"
                placeholder="Enter Answer"
              />
            </div>
          ))}
        </div>

        {showAddCustomQuestion && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <QuestionandanswerIcon className="w-[24px] h-[24px] text-verba-blue dark:text-verba-blue-dk" />
              <h2 className="text-base text-heading-text dark:text-heading-text-dk">
                Add Custom Question
              </h2>
            </div>
            <div className="flex items-center justify-between gap-2">
              <Input
                placeholder="Type your question"
                value={newQuestion}
                onChange={(e) => setNewQuestion(e?.target?.value)}
                secondaryContainerClassName="w-[475PX] border border-border-gray dark:border-border-gray-dk rounded-lg p-2"
                inputClassName="w-full"
              />
              <span className="w-px h-[10px] border-l border-border-gray dark:border-border-gray-dk" />
              <Button
                type="button"
                innerText="Cancel"
                buttonContainerClass="rounded-lg"
                onClick={() => {
                  setShowAddCustomQuestion(false);
                  setNewQuestion("");
                }}
                innerTextClass="text-md font-medium whitespace-nowrap text-verba-indigo dark:text-verba-indigo-dk"
              />
              <Button
                type="submit"
                loaderColor="blue"
                isLoading={isAddingViewingQA}
                disabled={isAddingViewingQA || newQuestion?.length === 0}
                onClick={handleAddViewingQA}
                innerText="Save"
                buttonContainerClass="rounded-lg"
                innerTextClass="text-md font-medium whitespace-nowrap text-verba-blue dark:text-verba-blue-dk"
              />
            </div>
          </div>
        )}

        <div className="flex justify-between items-center">
          <Button
            innerText="Add Custom Question"
            Icon={AddIcon}
            innerTextClass="text-md text-verba-blue dark:text-verba-blue-dk"
            buttonContainerClass={`rounded-[8px] flex items-center gap-[8px] ${
              showAddCustomQuestion ? "opacity-0" : ""
            }`}
            iconClass="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk"
            iconLeft={true}
            onClick={() => setShowAddCustomQuestion(!showAddCustomQuestion)}
          />

          <div className="flex gap-2">
            <Button
              type="button"
              innerText="Cancel"
              onClick={handleClose}
              buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
              innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
            />
            <Button
              type="submit"
              loaderColor="white"
              disabled={isSaveButtonDisabled}
              isLoading={isUpdatingViewingQA}
              innerText="Save"
              buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed hover:bg-verba-blue-hover dark:hover:bg-verba-blue-hover-dk"
              innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
              onClick={handleSubmit}
            />
          </div>
        </div>
      </div>
      )}
    </Drawer>
  );
};

export default ViewingQADrawer;
